import { measureReportText } from './fonts.js';
import { REPORT_ACCENT, reportSectionStyle } from './theme.js';
import { safeReportLink } from './links.js';
const INK = '#20252c';
const MUTED = '#68717d';
const LINE = '#e3e7eb';
const PAPER = '#f4f6f8';
function statusAssessment(value) {
    const key = String(value ?? '')
        .toLowerCase()
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');
    if (/^(completed?|done|finished)$/.test(key))
        return { label: 'Completed', kind: 'complete', color: '#17865c', tint: '#edf7f1' };
    if (/^(in progress|active|ongoing)$/.test(key))
        return {
            label: key === 'active' ? 'Active' : 'In progress',
            kind: 'progress',
            color: '#205bff',
            tint: '#eef3ff',
        };
    if (/^(not started|pending|planned)$/.test(key))
        return {
            label: key === 'not started' ? 'Not started' : key[0].toUpperCase() + key.slice(1),
            kind: 'planned',
            color: MUTED,
            tint: '#ffffff',
        };
    if (/^(blocked|overdue|at risk)$/.test(key))
        return {
            label: key[0].toUpperCase() + key.slice(1),
            kind: 'warning',
            color: '#b76413',
            tint: '#fff5e9',
        };
    if (/^cancell?ed$/.test(key))
        return { label: 'Cancelled', kind: 'cancelled', color: MUTED, tint: PAPER };
    if (/^(not recorded|not assessed|unavailable)$/.test(key))
        return {
            label: key[0].toUpperCase() + key.slice(1),
            kind: 'missing',
            color: MUTED,
            tint: PAPER,
        };
    return null;
}
function statusIcon(x, y, size, status) {
    const elements = [
        {
            kind: 'ellipse',
            x,
            y,
            width: size,
            height: size,
            fill: status.tint,
            stroke: status.color,
            strokeWidth: 1.2,
            statusIcon: true,
        },
    ];
    const line = (x1, y1, x2, y2) => elements.push({
        kind: 'line',
        x: x + size * x1,
        y: y + size * y1,
        x2: x + size * x2,
        y2: y + size * y2,
        color: status.color,
        width: 1.5,
        statusIcon: true,
    });
    if (status.kind === 'complete') {
        line(0.24, 0.51, 0.43, 0.7);
        line(0.43, 0.7, 0.78, 0.29);
    }
    else if (status.kind === 'progress')
        elements.push({
            kind: 'ellipse',
            x: x + size * 0.32,
            y: y + size * 0.32,
            width: size * 0.36,
            height: size * 0.36,
            fill: status.color,
            statusIcon: true,
        });
    else if (status.kind === 'warning') {
        line(0.5, 0.23, 0.5, 0.55);
        elements.push({
            kind: 'ellipse',
            x: x + size * 0.44,
            y: y + size * 0.68,
            width: size * 0.12,
            height: size * 0.12,
            fill: status.color,
            statusIcon: true,
        });
    }
    else if (status.kind === 'cancelled') {
        line(0.28, 0.28, 0.72, 0.72);
        line(0.28, 0.72, 0.72, 0.28);
    }
    else if (status.kind === 'missing')
        line(0.27, 0.5, 0.73, 0.5);
    return elements;
}
export function safeAccent(value) {
    return /^#[0-9a-f]{6}$/i.test(value) ? value : REPORT_ACCENT;
}
function inlineRuns(markdown, baseStyle = 'normal') {
    const text = String(markdown ?? '')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/<[^>]*>/g, '')
        .replace(/\\([*_`])/g, '$1');
    const exactLink = safeReportLink(text.trim());
    if (exactLink)
        return [{ text, style: baseStyle, hyperlink: exactLink }];
    return text
        .split(/(\[[^\]]+\]\((?:[^()]|\([^()]*\))*\)|https?:\/\/[^\s<>\[\]`]+|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`)/gi)
        .filter(Boolean)
        .flatMap((part) => {
        const link = part.match(/^\[([^\]]+)\]\((.*)\)$/);
        if (link)
            return inlineRuns(link[1], baseStyle).map((run) => ({
                ...run,
                hyperlink: safeReportLink(link[2]),
            }));
        if (/^https?:\/\//i.test(part)) {
            const value = part.replace(/[.,;!?)]+$/, '');
            const suffix = part.slice(value.length);
            return [
                { text: value, style: baseStyle, hyperlink: safeReportLink(value) },
                ...(suffix ? [{ text: suffix, style: baseStyle }] : []),
            ];
        }
        if (/^(\*\*|__)/.test(part))
            return inlineRuns(part.slice(2, -2), baseStyle === 'italic' ? 'bolditalic' : 'bold');
        if (/^(\*|_)/.test(part))
            return inlineRuns(part.slice(1, -1), baseStyle === 'bold' ? 'bolditalic' : 'italic');
        if (part.startsWith('`'))
            return [{ text: part.slice(1, -1), style: baseStyle }];
        return [{ text: part, style: baseStyle }];
    });
}
/** Wrap at measured glyph advances, including unusually long URLs/words. */
function wrapRuns(runs, width, size) {
    const result = [];
    let current = [];
    let used = 0;
    const commit = () => {
        while (current.length && !current[current.length - 1].text.trim())
            current.pop();
        if (current.length)
            result.push(current);
        current = [];
        used = 0;
    };
    const append = (text, style, hyperlink) => {
        const last = current[current.length - 1];
        if (last?.style === style && last.hyperlink === hyperlink)
            last.text += text;
        else
            current.push({ text, style, ...(hyperlink ? { hyperlink } : {}) });
        used += measureReportText(text, size, style);
    };
    for (const run of runs) {
        for (const token of run.text.match(/\S+|\s+/g) || []) {
            if (!token.trim()) {
                if (current.length && used + measureReportText(' ', size, run.style) <= width)
                    append(' ', run.style, run.hyperlink);
                continue;
            }
            const tokenWidth = measureReportText(token, size, run.style);
            if (used + tokenWidth > width && current.length)
                commit();
            if (tokenWidth <= width) {
                append(token, run.style, run.hyperlink);
                continue;
            }
            for (const character of Array.from(token)) {
                const charWidth = measureReportText(character, size, run.style);
                if (used + charWidth > width && current.length)
                    commit();
                append(character, run.style, run.hyperlink);
            }
        }
    }
    commit();
    return result.length ? result : [[{ text: '', style: 'normal' }]];
}
function textElement(text, x, y, width, size, style = 'normal', color = INK, align = 'left') {
    return {
        kind: 'text',
        x,
        y,
        width,
        height: size * 1.3,
        text,
        fontSize: size,
        fontStyle: style,
        color,
        align,
    };
}
class Composer {
    doc;
    view;
    pages = [];
    page;
    y = 0;
    sectionId = '';
    title = '';
    continuation = 0;
    slideId;
    notes;
    layout = 'narrative';
    tableCount = 0;
    width;
    height;
    margin;
    contentTop;
    bottom;
    bodySize;
    accent;
    constructor(doc, view) {
        this.doc = doc;
        this.view = view;
        this.width = view === 'report' ? 794 : 1280;
        this.height = view === 'report' ? 1123 : 720;
        this.margin = view === 'report' ? 56 : 72;
        this.contentTop = view === 'report' ? 176 : 172;
        this.bottom = this.height - (view === 'report' ? 80 : 64);
        this.bodySize = view === 'report' ? 15 : 23;
        this.accent = safeAccent(doc.theme.accent);
    }
    start(sectionId, title, slideId, notes, layout = 'narrative') {
        this.sectionId = sectionId;
        this.title = title;
        this.continuation = 0;
        this.slideId = slideId;
        this.notes = notes;
        this.layout = layout;
        this.nextPage();
    }
    nextPage() {
        this.page = {
            id: `${this.view}-${this.slideId || this.sectionId}-${this.continuation++}`,
            sectionId: this.sectionId,
            title: this.title,
            width: this.width,
            height: this.height,
            pageNumber: 0,
            elements: [],
            slideId: this.slideId,
            notes: this.notes,
        };
        this.pages.push(this.page);
        const company = this.doc.sourceSnapshot.company;
        const companyWidth = this.contentWidth - 250;
        const companySize = Math.min(12, companyWidth / Math.max(measureReportText(company.name, 1, 'bold'), 1));
        this.page.elements.push({ kind: 'rect', x: 0, y: 0, width: this.width, height: 8, fill: this.accent }, textElement(company.name, this.margin, 32, companyWidth, companySize, 'bold', MUTED));
        this.page.elements.push(textElement(this.view === 'report' ? 'STRATEGIC REPORT' : 'PRESENTATION', this.width - this.margin - (company.logo ? 240 : 160), 34, 150, 9, 'bold', MUTED, 'right'));
        if (this.layout !== 'narrative')
            this.page.elements.push({
                kind: 'rect',
                x: this.margin,
                y: 62,
                width: this.contentWidth,
                height: 2,
                fill: this.sectionStyle.color,
            });
        if (company.logo)
            this.page.elements.push({
                kind: 'image',
                x: this.width - this.margin - 60,
                y: 22,
                width: 60,
                height: 34,
                src: company.logo,
                alt: `${company.name} logo`,
            });
        const titleSize = this.view === 'report' ? 31 : 38;
        const titleLines = wrapRuns([{ text: this.title, style: 'bold' }], this.contentWidth, titleSize);
        const shown = titleLines.slice(0, 2);
        // A very long section title is rendered in full as a content heading below.
        for (let i = 0; i < shown.length; i++)
            this.line(shown[i], this.margin, 72 + i * titleSize * 1.2, titleSize);
        const titleBottom = 72 + shown.length * titleSize * 1.2 + titleSize * 0.1;
        const sectionLabel = `${this.sectionStyle.label.toUpperCase()}${this.continuation > 1 ? ' · CONTINUED' : ''}`;
        this.page.elements.push({
            kind: 'rect',
            x: this.margin,
            y: titleBottom + 8,
            width: 4,
            height: 13,
            fill: this.sectionStyle.color,
        }, textElement(sectionLabel, this.margin + 13, titleBottom + 8, this.contentWidth - 13, 10, 'bold', this.sectionStyle.color));
        this.y = Math.max(this.contentTop, titleBottom + 39);
        if (titleLines.length > 2 && this.continuation === 1)
            this.paragraph(this.title, { size: this.bodySize, style: 'bold' });
    }
    get contentWidth() {
        return this.width - this.margin * 2;
    }
    get sectionStyle() {
        return reportSectionStyle(this.sectionId);
    }
    ensure(height) {
        if (this.y + height > this.bottom && this.y > this.contentTop + 1)
            this.nextPage();
    }
    gap(height = 18) {
        this.y = Math.min(this.y + height, this.bottom);
    }
    line(runs, x, y, size, color = INK) {
        for (const run of runs) {
            const width = measureReportText(run.text, size, run.style);
            this.page.elements.push({
                ...textElement(run.text, x, y, width, size, run.style, color),
                ...(run.hyperlink ? { hyperlink: run.hyperlink } : {}),
            });
            x += width;
        }
    }
    paragraph(markdown, opts = {}) {
        const size = opts.size || this.bodySize;
        const indent = opts.indent || 0;
        const lines = wrapRuns(inlineRuns(markdown, opts.style), this.contentWidth - indent, size);
        const lineHeight = size * 1.45;
        for (const line of lines) {
            this.ensure(lineHeight);
            this.line(line, this.margin + indent, this.y, size, opts.color);
            this.y += lineHeight;
        }
        this.gap(opts.gap ?? 13);
    }
    paragraphHeight(markdown, size, gap = 13) {
        return wrapRuns(inlineRuns(markdown), this.contentWidth, size).length * size * 1.45 + gap;
    }
    radarMissingNotes(block) {
        return block.series.flatMap((item) => {
            const missing = block.axes.filter((_, index) => item.values[index] === null);
            const captionExplainsMissing = /not recorded|not assessed|unrated/i.test(block.caption) &&
                missing.every((axis) => block.caption.includes(axis));
            return missing.length && !captionExplainsMissing
                ? [`${item.label}: ${missing.join(', ')} — not assessed.`]
                : [];
        });
    }
    heading(title) {
        this.ensure(this.bodySize * 5);
        this.paragraph(title, { size: this.bodySize + 4, style: 'bold', color: this.accent, gap: 14 });
    }
    markdown(markdown) {
        const lines = markdown.replace(/\r\n/g, '\n').split('\n');
        let paragraph = [];
        const flush = () => {
            if (paragraph.length)
                this.paragraph(paragraph.join(' '));
            paragraph = [];
        };
        for (let index = 0; index < lines.length; index++) {
            const line = lines[index].trim();
            if (!line) {
                flush();
                continue;
            }
            if (/^\|/.test(line) && /^\|?[\s:|-]+\|?\s*$/.test(lines[index + 1]?.trim() || '')) {
                flush();
                const cells = (value) => value
                    .trim()
                    .replace(/^\||\|$/g, '')
                    .split('|')
                    .map((item) => item.trim());
                const columns = cells(line);
                const rows = [];
                index += 2;
                while (index < lines.length && /^\|/.test(lines[index].trim())) {
                    rows.push(cells(lines[index]));
                    index++;
                }
                index--;
                this.table(columns, rows);
                continue;
            }
            if (/^#{1,6}\s/.test(line)) {
                flush();
                this.heading(line.replace(/^#{1,6}\s+/, ''));
                continue;
            }
            if (/^[-*_]{3,}$/.test(line)) {
                flush();
                this.gap();
                continue;
            }
            const list = line.match(/^([-*+]\s+|\d+[.)]\s+)(.*)/);
            if (list) {
                flush();
                const marker = /^\d/.test(list[1]) ? list[1].trim() : '•';
                const wrapped = wrapRuns(inlineRuns(list[2]), this.contentWidth - 25, this.bodySize);
                for (let i = 0; i < wrapped.length; i++) {
                    this.ensure(this.bodySize * 1.45);
                    if (i === 0)
                        this.page.elements.push(textElement(marker, this.margin, this.y, 23, this.bodySize, 'normal', this.accent));
                    this.line(wrapped[i], this.margin + 25, this.y, this.bodySize);
                    this.y += this.bodySize * 1.45;
                }
                this.gap(7);
                continue;
            }
            if (line.startsWith('>')) {
                flush();
                this.paragraph(line.replace(/^>\s*/, ''), { style: 'italic', indent: 18, color: MUTED });
                continue;
            }
            paragraph.push(line.replace(/^```.*$/, ''));
        }
        flush();
    }
    takeaways(markdown) {
        if (!['matrix', 'scorecard', 'roadmap'].includes(this.layout) ||
            /(^|\n)\s*(#{1,6}\s|\||>|```)/.test(markdown)) {
            this.markdown(markdown);
            return;
        }
        const parts = markdown
            .split(/\n\s*\n|\n(?=\s*(?:[-*+]|\d+[.)])\s)/)
            .map((part) => part.trim().replace(/^(?:[-*+]|\d+[.)])\s+/, ''))
            .filter(Boolean);
        if (!parts.length)
            return;
        const columns = this.layout === 'matrix' ? 2 : this.layout === 'scorecard' ? Math.min(3, parts.length) : 1;
        const cardWidth = (this.contentWidth - (columns - 1) * 18) / columns;
        const size = this.view === 'slides' ? 21 : this.bodySize;
        const cards = parts.map((part) => {
            const pair = this.layout === 'matrix'
                ? part.match(/^(\*\*[^*]+\*\*|[^:–—]{1,45})\s*[:–—]\s*([\s\S]+)$/)
                : null;
            const label = pair ? pair[1].replace(/^(\*\*|__)|(\*\*|__)$/g, '') : '';
            const labels = label
                ? wrapRuns([{ text: label, style: 'bold' }], cardWidth - 36, size + 2)
                : [];
            const lines = wrapRuns(inlineRuns(pair ? pair[2] : part), cardWidth - 36, size);
            const height = 36 + labels.length * (size + 2) * 1.4 + lines.length * size * 1.45 + (label ? 8 : 0);
            return { labels, lines, height };
        });
        if (cards.some((card) => card.height > this.bottom - this.contentTop)) {
            // A user-authored long takeaway flows as text; never shrink or truncate it.
            this.markdown(markdown);
            return;
        }
        for (let row = 0; row < Math.ceil(cards.length / columns); row++) {
            const height = Math.max(...cards.slice(row * columns, (row + 1) * columns).map((card) => card.height));
            this.ensure(height + 18);
            for (let column = 0; column < columns; column++) {
                const card = cards[row * columns + column];
                if (!card)
                    continue;
                const x = this.margin + column * (cardWidth + 18);
                this.page.elements.push({
                    kind: 'rect',
                    x,
                    y: this.y,
                    width: cardWidth,
                    height,
                    fill: PAPER,
                    radius: 4,
                });
                this.page.elements.push({
                    kind: 'rect',
                    x,
                    y: this.y,
                    width: cardWidth,
                    height: 3,
                    fill: this.accent,
                });
                let y = this.y + 18;
                for (const line of card.labels) {
                    this.line(line, x + 18, y, size + 2, this.accent);
                    y += (size + 2) * 1.4;
                }
                if (card.labels.length)
                    y += 8;
                for (const line of card.lines) {
                    this.line(line, x + 18, y, size);
                    y += size * 1.45;
                }
            }
            this.y += height + 18;
        }
    }
    table(columns, rows, opts = {}) {
        const count = Math.max(columns.length, ...rows.map((row) => row.length), 0);
        if (!count)
            return;
        columns = Array.from({ length: count }, (_, index) => columns[index] || `Column ${index + 1}`);
        const pad = 10;
        const size = this.view === 'report' ? 12 : 18;
        const width = this.contentWidth / columns.length;
        const lineHeight = size * 1.4;
        const tableIdentity = `${this.sectionId}-table-${this.tableCount++}`;
        const iconSize = size + 2;
        const statusColumns = new Set(columns.flatMap((column, index) => (/^status$/i.test(column.trim()) ? [index] : [])));
        const wrapCell = (value, bold = false, icon = false, literal = false) => wrapRuns(literal
            ? [{ text: String(value ?? ''), style: bold ? 'bold' : 'normal' }]
            : inlineRuns(String(value ?? ''), bold ? 'bold' : 'normal'), Math.max(width - 2 * pad - (icon ? iconSize + 7 : 0), 12), size);
        const headers = columns.map((value, index) => wrapCell(value, true, false, opts.literalColumns?.includes(index)));
        const headerHeight = Math.max(...headers.map((lines) => lines.length)) * lineHeight + pad * 2;
        const available = this.bottom - this.contentTop;
        if (headerHeight > available / 2 || width < 2 * pad + size * 3) {
            // Extremely narrow/long headers cannot form a readable table. Preserve all
            // values as a labelled record layout rather than clipping any cells.
            if (!rows.length)
                this.paragraph(columns.join(' · '), { style: 'bold' });
            for (const row of rows) {
                for (let i = 0; i < columns.length; i++)
                    this.paragraph(`**${columns[i]}:** ${row[i] ?? ''}`);
                this.gap();
            }
            return;
        }
        const drawRow = (cells, offset, count, header, statuses = []) => {
            const rowHeight = count * lineHeight + pad * 2;
            const id = `${tableIdentity}-${this.page.id}`;
            this.page.tables ||= [];
            let table = this.page.tables.find((item) => item.id === id);
            if (!table) {
                table = {
                    id,
                    x: this.margin,
                    y: this.y,
                    width: this.contentWidth,
                    height: 0,
                    fontSize: size,
                    columnWidths: columns.map(() => width),
                    accent: this.sectionStyle.color,
                    tint: this.sectionStyle.tint,
                    rows: [],
                };
                this.page.tables.push(table);
            }
            table.rows.push({
                height: rowHeight,
                header,
                cells: cells.map((lines) => lines.slice(offset, offset + count)),
                iconColumns: statuses.flatMap((status, index) => (status ? [index] : [])),
            });
            table.height += rowHeight;
            const firstElement = this.page.elements.length;
            for (let col = 0; col < columns.length; col++) {
                const x = this.margin + col * width;
                this.page.elements.push({
                    kind: 'rect',
                    x,
                    y: this.y,
                    width,
                    height: rowHeight,
                    fill: header ? this.sectionStyle.tint : '#ffffff',
                    stroke: LINE,
                });
                for (let line = 0; line < count; line++) {
                    const runs = cells[col]?.[offset + line];
                    if (runs)
                        this.line(runs, x + pad + (statuses[col] ? iconSize + 7 : 0), this.y + pad + line * lineHeight, size, header ? this.sectionStyle.color : INK);
                }
                if (offset === 0 && statuses[col]) {
                    const icons = statusIcon(x + pad, this.y + pad + 1, iconSize, statuses[col]);
                    icons.forEach((element) => {
                        element.tableOverlay = true;
                    });
                    this.page.elements.push(...icons);
                }
            }
            this.page.elements.slice(firstElement).forEach((element) => {
                element.tableId = id;
            });
            this.y += rowHeight;
        };
        const drawHeader = () => {
            this.ensure(headerHeight + lineHeight + 2 * pad);
            drawRow(headers, 0, Math.max(...headers.map((lines) => lines.length)), true);
        };
        drawHeader();
        for (const row of rows) {
            const statuses = columns.map((_, index) => statusColumns.has(index) ? statusAssessment(row[index] ?? '') : null);
            const cells = columns.map((_, i) => wrapCell(statuses[i]?.label || String(row[i] ?? ''), false, !!statuses[i], opts.literalColumns?.includes(i)));
            const count = Math.max(...cells.map((lines) => lines.length), 1);
            let offset = 0;
            while (offset < count) {
                let capacity = Math.floor((this.bottom - this.y - 2 * pad) / lineHeight);
                if (capacity < 1) {
                    this.nextPage();
                    drawHeader();
                    capacity = Math.floor((this.bottom - this.y - 2 * pad) / lineHeight);
                }
                const take = Math.min(capacity, count - offset);
                drawRow(cells, offset, take, false, statuses);
                offset += take;
                if (offset < count) {
                    this.nextPage();
                    drawHeader();
                }
            }
        }
        this.gap(22);
    }
    comparison(block) {
        const positiveColor = this.accent;
        const negativeColor = this.accent.toLowerCase() === '#8934ed' ? REPORT_ACCENT : '#8934ed';
        if (!block.items.length) {
            this.paragraph('No findings have been recorded for this comparison.', { color: MUTED });
            if (block.caption)
                this.paragraph(block.caption, { size: this.bodySize - 2, color: MUTED });
            return;
        }
        const size = this.view === 'slides' ? 17 : 13;
        const labelWidth = this.contentWidth * 0.3;
        const barWidth = this.contentWidth - labelWidth - 70;
        const maximum = Math.max(1, ...block.items.flatMap((item) => [item.positive, item.negative]));
        const barHeight = 12;
        const items = block.items.map((item) => ({
            item,
            labels: wrapRuns(inlineRuns(item.label), labelWidth, size),
        }));
        let index = 0;
        while (index < items.length) {
            this.ensure(100);
            for (const [label, fill, offset] of [
                ['Positive', positiveColor, 0],
                ['Negative', negativeColor, 140],
            ]) {
                this.page.elements.push({
                    kind: 'rect',
                    x: this.margin + offset,
                    y: this.y + 4,
                    width: 14,
                    height: 8,
                    fill,
                });
                this.page.elements.push(textElement(label, this.margin + offset + 23, this.y, 110, size, 'normal', MUTED));
            }
            this.y += size * 1.4 + 15;
            let rowHeight = Math.max(52, items[index].labels.length * size * 1.4 + 14);
            if (rowHeight > this.bottom - this.contentTop - 50) {
                this.paragraph(items[index].item.label);
                items[index].labels = [];
                rowHeight = 52;
            }
            this.ensure(rowHeight);
            const group = [items[index++]];
            while (index < items.length) {
                const nextHeight = Math.max(rowHeight, items[index].labels.length * size * 1.4 + 14);
                if (this.y + (group.length + 1) * nextHeight > this.bottom)
                    break;
                rowHeight = nextHeight;
                group.push(items[index++]);
            }
            const chartId = `${block.id}-${this.page.id}-${index}`;
            const x = this.margin + labelWidth + 16;
            const chartY = this.y;
            group.forEach(({ item, labels }, row) => {
                const y = chartY + row * rowHeight;
                labels.forEach((line, lineIndex) => this.line(line, this.margin, y + 9 + lineIndex * size * 1.4, size));
                for (const [value, fill, offset] of [
                    [item.positive, positiveColor, -10],
                    [item.negative, negativeColor, 10],
                ]) {
                    const barY = y + rowHeight / 2 + offset - barHeight / 2;
                    this.page.elements.push({
                        kind: 'rect',
                        x,
                        y: barY,
                        width: barWidth,
                        height: barHeight,
                        fill: LINE,
                    });
                    if (value > 0)
                        this.page.elements.push({
                            kind: 'rect',
                            x,
                            y: barY,
                            width: (barWidth * value) / maximum,
                            height: barHeight,
                            fill,
                            chartId,
                        });
                    this.page.elements.push(textElement(String(value), x + barWidth + 12, barY - 3, 40, size, 'normal', MUTED));
                }
            });
            this.page.charts ||= [];
            this.page.charts.push({
                kind: 'comparison',
                id: chartId,
                x,
                y: chartY,
                width: barWidth,
                height: group.length * rowHeight,
                maximum,
                rowHeight,
                barHeight,
                positiveColor,
                negativeColor,
                items: group.map((item) => item.item),
            });
            this.y += group.length * rowHeight;
            if (index < items.length)
                this.nextPage();
        }
        this.gap(14);
        if (block.caption)
            this.paragraph(block.caption, { size: this.bodySize - 2, color: MUTED });
    }
    radar(block) {
        if (block.axes.length < 3 || block.axes.length > 12 || !block.series.length) {
            if (!block.axes.length || !block.series.length)
                this.paragraph('No assessments have been recorded for this chart.', { color: MUTED });
            else
                this.table(['Dimension', ...block.series.map((series) => series.label)], block.axes.map((axis, index) => [
                    axis,
                    ...block.series.map((series) => series.values[index] === null ? 'Not assessed' : String(series.values[index])),
                ]));
            if (block.caption)
                this.paragraph(block.caption, { size: this.bodySize - 2, color: MUTED });
            return;
        }
        const size = this.view === 'slides' ? 16 : 13;
        const labelWidth = this.view === 'slides' ? 215 : 170;
        const radius = this.view === 'slides' ? 108 : 132;
        const captionSize = this.view === 'slides' ? 14 : this.bodySize - 2;
        const colors = [
            this.accent,
            ...['#8934ed', REPORT_ACCENT, '#17865c', '#b76413'].filter((color) => color.toLowerCase() !== this.accent.toLowerCase()),
        ];
        const series = block.series.map((item, index) => ({
            ...item,
            color: colors[index % colors.length],
        }));
        const labels = block.axes.map((axis, index) => {
            let lines = wrapRuns(inlineRuns(axis), labelWidth, size);
            if (lines.length > 3) {
                this.paragraph(`Dimension ${index + 1}: ${axis}`, { size, color: MUTED });
                lines = wrapRuns(inlineRuns(`Dimension ${index + 1}`), labelWidth, size);
            }
            return lines;
        });
        const legendWidth = this.contentWidth / 2 - labelWidth / 2 - 40;
        const inlineLegend = series.length <= 2 &&
            series.every((item) => measureReportText(item.label, size) <= legendWidth);
        if (!inlineLegend)
            for (const item of series) {
                this.ensure(size * 1.45 + 14);
                this.page.elements.push({
                    kind: 'rect',
                    x: this.margin,
                    y: this.y + 5,
                    width: 14,
                    height: 8,
                    fill: item.color,
                });
                const lines = wrapRuns(inlineRuns(item.label), this.contentWidth - 25, size);
                for (const line of lines) {
                    this.ensure(size * 1.45);
                    this.line(line, this.margin + 25, this.y, size, MUTED);
                    this.y += size * 1.45;
                }
                this.gap(8);
            }
        const labelHeight = Math.max(...labels.map((lines) => lines.length * size * 1.4));
        const diagramHeight = radius * 2 + labelHeight * 2 + 36;
        this.ensure(diagramHeight);
        if (inlineLegend)
            series.forEach((item, index) => {
                const y = this.y + index * (size * 1.45 + 6);
                this.page.elements.push({
                    kind: 'rect',
                    x: this.margin,
                    y: y + 5,
                    width: 14,
                    height: 8,
                    fill: item.color,
                });
                this.line(inlineRuns(item.label), this.margin + 25, y, size, MUTED);
            });
        const centerX = this.margin + this.contentWidth / 2;
        const centerY = this.y + labelHeight + 18 + radius;
        const points = block.axes.map((_, index) => {
            const angle = -Math.PI / 2 + (index * Math.PI * 2) / block.axes.length;
            return { dx: Math.cos(angle), dy: Math.sin(angle) };
        });
        const line = (x, y, x2, y2, color, width = 1, chartId) => this.page.elements.push({ kind: 'line', x, y, x2, y2, color, width, chartId });
        const majorUnit = Number.isInteger(block.maximum)
            ? block.maximum <= 5
                ? 1
                : block.maximum <= 10
                    ? 2
                    : block.maximum <= 20
                        ? 5
                        : Math.ceil(block.maximum / 5)
            : block.maximum / 4;
        const ticks = Array.from({ length: Math.floor(block.maximum / majorUnit) }, (_, index) => (index + 1) * majorUnit);
        if (ticks[ticks.length - 1] !== block.maximum)
            ticks.push(block.maximum);
        for (const tick of ticks) {
            const scale = tick / block.maximum;
            points.forEach((point, index) => {
                const next = points[(index + 1) % points.length];
                line(centerX + point.dx * radius * scale, centerY + point.dy * radius * scale, centerX + next.dx * radius * scale, centerY + next.dy * radius * scale, LINE);
            });
            this.page.elements.push(textElement(String(Number(tick.toFixed(2))), centerX + 6, centerY - radius * scale + 3, 40, 10, 'normal', MUTED));
        }
        points.forEach((point, index) => {
            line(centerX, centerY, centerX + point.dx * radius, centerY + point.dy * radius, LINE);
            const lines = labels[index];
            const height = lines.length * size * 1.4;
            const positionX = centerX + point.dx * (radius + 18);
            let x = positionX + (point.dx > 0.25 ? 8 : point.dx < -0.25 ? -labelWidth - 8 : -labelWidth / 2);
            x = Math.max(this.margin, Math.min(x, this.width - this.margin - labelWidth));
            const y = centerY +
                point.dy * (radius + 18) -
                (point.dy < -0.4 ? height : point.dy > 0.4 ? 0 : height / 2);
            lines.forEach((runs, lineIndex) => this.line(runs, x, y + lineIndex * size * 1.4, size));
        });
        const chartId = `${block.id}-${this.page.id}`;
        for (const item of series) {
            const positions = item.values.map((value, index) => value === null
                ? null
                : {
                    x: centerX + (points[index].dx * radius * value) / block.maximum,
                    y: centerY + (points[index].dy * radius * value) / block.maximum,
                });
            positions.forEach((point, index) => {
                if (!point)
                    return;
                const next = positions[(index + 1) % positions.length];
                if (next)
                    line(point.x, point.y, next.x, next.y, item.color, 2, chartId);
                this.page.elements.push({
                    kind: 'ellipse',
                    x: point.x - 3,
                    y: point.y - 3,
                    width: 6,
                    height: 6,
                    fill: item.color,
                    chartId,
                });
            });
        }
        this.page.charts ||= [];
        this.page.charts.push({
            kind: 'radar',
            id: chartId,
            x: centerX - radius,
            y: centerY - radius,
            width: radius * 2,
            height: radius * 2,
            maximum: block.maximum,
            majorUnit,
            axes: block.axes,
            series,
        });
        this.y += diagramHeight + 16;
        for (const note of this.radarMissingNotes(block))
            this.paragraph(note, { size: this.view === 'slides' ? 14 : this.bodySize - 3, color: MUTED });
        if (block.caption)
            this.paragraph(block.caption, { size: captionSize, color: MUTED });
    }
    positioningRows(block) {
        const size = this.view === 'slides' ? 17 : 12;
        const axisWidth = this.view === 'slides' ? 112 : 96;
        const cellWidth = (this.contentWidth - axisWidth) / 4;
        return ['high', 'medium', 'low'].map((priceQuality) => {
            const items = block.items.filter((item) => item.priceQuality === priceQuality && item.geoScope !== null);
            const contents = ['local', 'regional', 'national', 'international'].map((geoScope) => {
                const companies = items.filter((item) => item.geoScope === geoScope);
                const lines = [];
                const spans = [];
                companies.forEach((item, index) => {
                    if (index)
                        lines.push([]);
                    const start = lines.length;
                    lines.push(...wrapRuns([{ text: item.label, style: 'bold' }], cellWidth - 24, size), ...wrapRuns(inlineRuns(`Market share: ${item.marketSize === null ? 'not assessed' : `${item.marketSize}%`}`), cellWidth - 24, size));
                    spans.push({ item, start, end: lines.length });
                });
                return {
                    lines: lines.length ? lines : [[{ text: 'None recorded', style: 'normal' }]],
                    spans,
                };
            });
            const cells = contents.map((content) => content.lines);
            return {
                priceQuality,
                items,
                cells,
                spans: contents.flatMap((content) => content.spans),
                lineCount: Math.max(2, ...cells.map((cell) => cell.length)),
            };
        });
    }
    positioning(block) {
        const size = this.view === 'slides' ? 17 : 12;
        const lineHeight = size * 1.4;
        const axisWidth = this.view === 'slides' ? 112 : 96;
        const cellWidth = (this.contentWidth - axisWidth) / 4;
        const pad = 12;
        const headerHeight = Math.max(2, wrapRuns(inlineRuns('Price / quality', 'bold'), axisWidth - 2 * pad, size).length) *
            lineHeight +
            2 * pad;
        const rows = this.positioningRows(block);
        const positioned = block.items.filter((item) => item.priceQuality !== null && item.geoScope !== null);
        const missing = block.items.filter((item) => item.priceQuality === null || item.geoScope === null);
        const registerChart = (items, y = this.y, height = 0) => {
            this.page.charts ||= [];
            let chart = this.page.charts.find((chart) => chart.kind === 'positioning' && chart.id === `${block.id}-${this.page.id}`);
            if (!chart) {
                chart = {
                    kind: 'positioning',
                    id: `${block.id}-${this.page.id}`,
                    x: this.margin,
                    y,
                    width: this.contentWidth,
                    height,
                    items: [],
                };
                this.page.charts.push(chart);
            }
            chart.items = Array.from(new Set([...chart.items, ...items]));
            chart.height = Math.max(chart.height, y + height - chart.y);
        };
        if (!block.items.length) {
            registerChart([]);
            this.paragraph('No competitive positioning data is available in this snapshot.', {
                color: MUTED,
            });
        }
        else if (!positioned.length) {
            this.paragraph('No competitors have complete price / quality and geographic scope assessments.', { color: MUTED });
        }
        else {
            const header = () => {
                this.ensure(headerHeight + 2 * pad + lineHeight);
                const y = this.y;
                ['Price / quality', 'Local', 'Regional', 'National', 'International'].forEach((label, index) => {
                    const x = this.margin + (index ? axisWidth + (index - 1) * cellWidth : 0);
                    const width = index ? cellWidth : axisWidth;
                    this.page.elements.push({
                        kind: 'rect',
                        x,
                        y,
                        width,
                        height: headerHeight,
                        fill: this.sectionStyle.tint,
                        stroke: LINE,
                    });
                    wrapRuns(inlineRuns(label, 'bold'), width - 2 * pad, size).forEach((line, lineIndex) => this.line(line, x + pad, y + pad + lineIndex * lineHeight, size, this.sectionStyle.color));
                });
                this.y += headerHeight;
            };
            header();
            for (const row of rows) {
                let offset = 0;
                const fullRowHeight = row.lineCount * lineHeight + 2 * pad;
                if (this.y + fullRowHeight > this.bottom &&
                    headerHeight + fullRowHeight <= this.bottom - this.contentTop) {
                    this.nextPage();
                    header();
                }
                while (offset < row.lineCount) {
                    let capacity = Math.floor((this.bottom - this.y - 2 * pad) / lineHeight);
                    if (capacity < 1) {
                        this.nextPage();
                        header();
                        capacity = Math.floor((this.bottom - this.y - 2 * pad) / lineHeight);
                    }
                    const count = Math.min(capacity, row.lineCount - offset);
                    const y = this.y;
                    const height = count * lineHeight + 2 * pad;
                    this.page.elements.push({
                        kind: 'rect',
                        x: this.margin,
                        y,
                        width: axisWidth,
                        height,
                        fill: PAPER,
                        stroke: LINE,
                    });
                    this.line(inlineRuns(row.priceQuality[0].toUpperCase() + row.priceQuality.slice(1), 'bold'), this.margin + pad, y + pad, size, INK);
                    if (offset && count > 1)
                        this.line(inlineRuns('Continued'), this.margin + pad, y + pad + lineHeight, Math.max(10, size - 2), MUTED);
                    row.cells.forEach((cell, column) => {
                        const x = this.margin + axisWidth + column * cellWidth;
                        this.page.elements.push({
                            kind: 'rect',
                            x,
                            y,
                            width: cellWidth,
                            height,
                            fill: '#ffffff',
                            stroke: LINE,
                        });
                        cell
                            .slice(offset, offset + count)
                            .forEach((line, lineIndex) => this.line(line, x + pad, y + pad + lineIndex * lineHeight, size, line.some((run) => run.style === 'bold') ? INK : MUTED));
                    });
                    registerChart(row.spans
                        .filter((span) => span.start < offset + count && span.end > offset)
                        .map((span) => span.item), y, height);
                    this.y += height;
                    offset += count;
                    if (offset < row.lineCount) {
                        this.nextPage();
                        header();
                    }
                }
            }
            this.gap(18);
        }
        if (missing.length) {
            this.heading('Not assessed for positioning');
            const firstPage = this.pages.length - 1;
            this.table(['Competitor', 'Price / quality', 'Geographic scope', 'Market share'], missing.map((item) => [
                item.label,
                item.priceQuality || 'Not assessed',
                item.geoScope || 'Not assessed',
                item.marketSize === null ? 'Not assessed' : `${item.marketSize}%`,
            ]), { literalColumns: [0] });
            for (const page of this.pages.slice(firstPage)) {
                page.charts ||= [];
                page.charts.push({
                    kind: 'positioning',
                    id: `${block.id}-${page.id}-unassessed`,
                    x: this.margin,
                    y: this.contentTop,
                    width: this.contentWidth,
                    height: 0,
                    items: missing,
                });
            }
        }
        if (block.caption)
            this.paragraph(block.caption, {
                size: this.view === 'slides' ? 14 : this.bodySize - 2,
                color: MUTED,
            });
    }
    organisation(block) {
        const children = new Map();
        for (const node of block.nodes)
            if (node.parentId !== null) {
                const group = children.get(node.parentId) || [];
                group.push(node);
                children.set(node.parentId, group);
            }
        const queue = block.nodes.filter((node) => node.parentId === null);
        const ordered = [];
        const seen = new Set();
        for (let index = 0; index < queue.length; index++) {
            const node = queue[index];
            if (seen.has(node.id))
                continue;
            seen.add(node.id);
            ordered.push(node);
            queue.push(...(children.get(node.id) || []));
        }
        let size = this.view === 'report' ? 14 : 18;
        const gap = 18, capacity = this.view === 'report' ? 3 : 4;
        const parentWidth = this.view === 'report' ? 340 : 400;
        const cardLines = (node, width) => [
            ...wrapRuns([{ text: node.label.replace(/\s+/g, ' ').trim(), style: 'bold' }], width - 24, size),
            ...wrapRuns([{ text: node.detail.replace(/\s+/g, ' ').trim(), style: 'normal' }], width - 24, size),
        ];
        const cardHeight = (lines) => 24 + lines.length * size * 1.4;
        const card = (node, x, y, width, height, parent) => {
            this.page.elements.push({
                kind: 'rect',
                x,
                y,
                width,
                height,
                fill: parent ? this.sectionStyle.tint : '#ffffff',
                stroke: parent ? this.sectionStyle.color : LINE,
                radius: 6,
            });
            cardLines(node, width).forEach((runs, index) => this.line(runs, x + 12, y + 12 + index * size * 1.4, size, INK));
        };
        // Small organisations fit as one complete hierarchy. Wider/deeper structures
        // use direct-parent panels, retaining every relationship across continuation pages.
        const levels = [], depths = new Map();
        for (const node of ordered) {
            const depth = node.parentId === null ? 0 : (depths.get(node.parentId) || 0) + 1;
            depths.set(node.id, depth);
            (levels[depth] ||= []).push(node);
        }
        if (levels.length && levels.every((level) => level.length <= capacity)) {
            const widths = levels.map((level) => Math.min(parentWidth, (this.contentWidth - (level.length - 1) * gap) / level.length));
            const heights = levels.map((level, index) => Math.max(...level.map((node) => cardHeight(cardLines(node, widths[index])))));
            const height = heights.reduce((sum, value) => sum + value, 0) + (levels.length - 1) * 42 + 26;
            const captionHeight = block.caption
                ? this.paragraphHeight(block.caption, this.view === 'slides' ? 14 : 12)
                : 0;
            if (this.y + height + captionHeight <= this.bottom) {
                const boxes = new Map();
                let y = this.y;
                levels.forEach((level, index) => {
                    const width = widths[index], rowHeight = heights[index];
                    const start = this.margin +
                        (this.contentWidth - (level.length * width + (level.length - 1) * gap)) / 2;
                    level.forEach((node, childIndex) => boxes.set(node.id, {
                        node,
                        x: start + childIndex * (width + gap),
                        y,
                        width,
                        height: rowHeight,
                    }));
                    y += rowHeight + 42;
                });
                for (const box of boxes.values()) {
                    const parent = box.node.parentId ? boxes.get(box.node.parentId) : undefined;
                    if (!parent)
                        continue;
                    const x = parent.x + parent.width / 2, x2 = box.x + box.width / 2, middleY = parent.y + parent.height + 21;
                    this.page.elements.push({
                        kind: 'line',
                        x,
                        y: parent.y + parent.height,
                        x2: x,
                        y2: middleY,
                        color: this.sectionStyle.color,
                        width: 1.5,
                    }, {
                        kind: 'line',
                        x,
                        y: middleY,
                        x2,
                        y2: middleY,
                        color: this.sectionStyle.color,
                        width: 1.5,
                    }, {
                        kind: 'line',
                        x: x2,
                        y: middleY,
                        x2,
                        y2: box.y,
                        color: this.sectionStyle.color,
                        width: 1.5,
                    });
                }
                for (const box of boxes.values())
                    card(box.node, box.x, box.y, box.width, box.height, box.node.parentId === null);
                this.y += height;
                if (block.caption)
                    this.paragraph(block.caption, { size: this.view === 'slides' ? 14 : 12, color: MUTED });
                return;
            }
        }
        for (const parent of ordered) {
            const reports = children.get(parent.id) || [];
            // Leaves already appear in their parent's panel. Isolated roots still render.
            if (!reports.length && parent.parentId !== null)
                continue;
            for (let offset = 0; offset < Math.max(1, reports.length); offset += capacity) {
                const group = reports.slice(offset, offset + capacity);
                const childWidth = Math.min(parentWidth, (this.contentWidth - Math.max(0, group.length - 1) * gap) / Math.max(1, group.length));
                size = this.view === 'report' ? 14 : 18;
                const connectorGap = 42;
                const panelHeight = () => cardHeight(cardLines(parent, parentWidth)) +
                    (group.length
                        ? connectorGap +
                            Math.max(...group.map((node) => cardHeight(cardLines(node, childWidth))))
                        : 0) +
                    26;
                while (panelHeight() > this.bottom - this.contentTop && size > 8)
                    size--;
                const topHeight = cardHeight(cardLines(parent, parentWidth));
                const childHeight = group.length
                    ? Math.max(...group.map((node) => cardHeight(cardLines(node, childWidth))))
                    : 0;
                const height = panelHeight();
                this.ensure(height);
                const topX = this.margin + (this.contentWidth - parentWidth) / 2, topY = this.y;
                const childY = topY + topHeight + connectorGap;
                const startX = this.margin +
                    (this.contentWidth - (group.length * childWidth + Math.max(0, group.length - 1) * gap)) /
                        2;
                if (group.length) {
                    const center = topX + parentWidth / 2, middleY = topY + topHeight + connectorGap / 2;
                    this.page.elements.push({
                        kind: 'line',
                        x: center,
                        y: topY + topHeight,
                        x2: center,
                        y2: middleY,
                        color: this.sectionStyle.color,
                        width: 1.5,
                    });
                    group.forEach((node, index) => {
                        const x = startX + index * (childWidth + gap), childCenter = x + childWidth / 2;
                        this.page.elements.push({
                            kind: 'line',
                            x: center,
                            y: middleY,
                            x2: childCenter,
                            y2: middleY,
                            color: this.sectionStyle.color,
                            width: 1.5,
                        }, {
                            kind: 'line',
                            x: childCenter,
                            y: middleY,
                            x2: childCenter,
                            y2: childY,
                            color: this.sectionStyle.color,
                            width: 1.5,
                        });
                        card(node, x, childY, childWidth, childHeight, false);
                    });
                }
                card(parent, topX, topY, parentWidth, topHeight, true);
                this.y += height;
            }
        }
        if (block.caption)
            this.paragraph(block.caption, { size: this.view === 'slides' ? 14 : 12, color: MUTED });
    }
    block(block) {
        if (block.kind === 'text' && !block.markdown.trim())
            return;
        const firstPage = this.pages.length - 1;
        const firstElement = this.page.elements.length;
        if (block.kind === 'positioning' &&
            block.items.some((item) => item.priceQuality !== null && item.geoScope !== null)) {
            const size = this.view === 'slides' ? 17 : 12;
            const axisWidth = this.view === 'slides' ? 112 : 96;
            const titleHeight = block.title
                ? wrapRuns(inlineRuns(block.title, 'bold'), this.contentWidth, this.bodySize + 4).length *
                    (this.bodySize + 4) *
                    1.45 +
                    14
                : 0;
            const headerHeight = Math.max(2, wrapRuns(inlineRuns('Price / quality', 'bold'), axisWidth - 24, size).length) *
                size *
                1.4 +
                24;
            const matrixHeight = this.positioningRows(block).reduce((height, row) => height + row.lineCount * size * 1.4 + 24, headerHeight);
            const captionHeight = block.caption
                ? this.paragraphHeight(block.caption, this.view === 'slides' ? 14 : this.bodySize - 2)
                : 0;
            const missing = block.items.filter((item) => item.priceQuality === null || item.geoScope === null);
            const tableSize = this.view === 'slides' ? 18 : 12;
            const wrapTableValue = (value, bold = false, literal = false) => wrapRuns(literal
                ? [{ text: value, style: bold ? 'bold' : 'normal' }]
                : inlineRuns(value, bold ? 'bold' : 'normal'), this.contentWidth / 4 - 20, tableSize).length;
            const missingHeight = missing.length
                ? this.paragraphHeight('Not assessed for positioning', this.bodySize + 4, 14) +
                    Math.max(...['Competitor', 'Price / quality', 'Geographic scope', 'Market share'].map((label) => wrapTableValue(label, true))) *
                        tableSize *
                        1.4 +
                    20 +
                    missing.reduce((height, item) => height +
                        Math.max(1, ...[
                            item.label,
                            item.priceQuality || 'Not assessed',
                            item.geoScope || 'Not assessed',
                            item.marketSize === null ? 'Not assessed' : `${item.marketSize}%`,
                        ].map((value, index) => wrapTableValue(value, false, index === 0))) *
                            tableSize *
                            1.4 +
                        20, 0) +
                    22
                : 0;
            const height = titleHeight + matrixHeight + 18 + missingHeight + captionHeight;
            if (height <= this.bottom - this.contentTop)
                this.ensure(height);
        }
        if (block.kind === 'comparison' && block.items.length) {
            const size = this.view === 'slides' ? 17 : 13;
            const rowHeight = Math.max(52, ...block.items.map((item) => wrapRuns(inlineRuns(item.label), this.contentWidth * 0.3, size).length * size * 1.4 + 14));
            const titleHeight = block.title
                ? wrapRuns(inlineRuns(block.title, 'bold'), this.contentWidth, this.bodySize + 4).length *
                    (this.bodySize + 4) *
                    1.45 +
                    14
                : 0;
            const height = titleHeight +
                size * 1.4 +
                15 +
                rowHeight * block.items.length +
                14 +
                (block.caption ? this.paragraphHeight(block.caption, this.bodySize - 2) : 0);
            if (height <= this.bottom - this.contentTop)
                this.ensure(height);
        }
        if (block.kind === 'radar' &&
            block.axes.length >= 3 &&
            block.axes.length <= 12 &&
            block.series.length) {
            const size = this.view === 'slides' ? 16 : 13;
            const labelWidth = this.view === 'slides' ? 215 : 170;
            const lines = block.axes.map((axis) => wrapRuns(inlineRuns(axis), labelWidth, size).length);
            const legendWidth = this.contentWidth / 2 - labelWidth / 2 - 40;
            const inlineLegend = block.series.length <= 2 &&
                block.series.every((item) => measureReportText(item.label, size) <= legendWidth);
            const titleHeight = block.title
                ? wrapRuns(inlineRuns(block.title, 'bold'), this.contentWidth, this.bodySize + 4).length *
                    (this.bodySize + 4) *
                    1.45 +
                    14
                : 0;
            const axisNotesHeight = block.axes.reduce((height, axis, index) => height +
                (lines[index] > 3 ? this.paragraphHeight(`Dimension ${index + 1}: ${axis}`, size) : 0), 0);
            const legendHeight = inlineLegend
                ? 0
                : block.series.reduce((height, item) => height +
                    wrapRuns(inlineRuns(item.label), this.contentWidth - 25, size).length * size * 1.45 +
                    8, 0);
            const radius = this.view === 'slides' ? 108 : 132;
            const labelHeight = Math.max(...lines.map((count, index) => count > 3
                ? wrapRuns(inlineRuns(`Dimension ${index + 1}`), labelWidth, size).length
                : count)) *
                size *
                1.4;
            const missingHeight = this.radarMissingNotes(block).reduce((height, note) => height + this.paragraphHeight(note, this.view === 'slides' ? 14 : this.bodySize - 3), 0);
            const captionHeight = block.caption
                ? this.paragraphHeight(block.caption, this.view === 'slides' ? 14 : this.bodySize - 2)
                : 0;
            const height = titleHeight +
                axisNotesHeight +
                legendHeight +
                radius * 2 +
                labelHeight * 2 +
                52 +
                missingHeight +
                captionHeight;
            if (height <= this.bottom - this.contentTop)
                this.ensure(height);
        }
        if ('title' in block && block.title)
            this.heading(block.title);
        switch (block.kind) {
            case 'text':
                this.markdown(block.markdown);
                break;
            case 'table':
                this.table(block.columns, block.rows);
                break;
            case 'organisation':
                this.organisation(block);
                break;
            case 'comparison':
                this.comparison(block);
                break;
            case 'radar':
                this.radar(block);
                break;
            case 'positioning':
                this.positioning(block);
                break;
            case 'metrics': {
                if (this.layout === 'scorecard' || this.layout === 'visual') {
                    const columns = Math.min(this.view === 'report' ? 2 : 4, block.items.length);
                    const cellWidth = columns
                        ? (this.contentWidth - (columns - 1) * 16) / columns
                        : this.contentWidth;
                    const labelSize = this.bodySize - 2;
                    const valueSize = this.bodySize + 13;
                    const cards = block.items.map((item) => ({
                        status: statusAssessment(item.value) || statusAssessment(item.label),
                        labels: wrapRuns(inlineRuns(item.label, 'bold'), cellWidth -
                            32 -
                            (statusAssessment(item.value) || statusAssessment(item.label) ? labelSize + 9 : 0), labelSize),
                        values: wrapRuns(inlineRuns(item.value, 'bold'), cellWidth - 32, valueSize),
                        details: item.detail
                            ? wrapRuns(inlineRuns(item.detail), cellWidth - 32, labelSize)
                            : [],
                    }));
                    const heights = cards.map((card) => 34 +
                        card.labels.length * labelSize * 1.4 +
                        card.values.length * valueSize * 1.3 +
                        card.details.length * labelSize * 1.4);
                    if (heights.every((height) => height <= this.bottom - this.contentTop)) {
                        for (let row = 0; row < Math.ceil(cards.length / columns); row++) {
                            const height = Math.max(...heights.slice(row * columns, (row + 1) * columns));
                            this.ensure(height + 16);
                            for (let column = 0; column < columns; column++) {
                                const card = cards[row * columns + column];
                                if (!card)
                                    continue;
                                const x = this.margin + column * (cellWidth + 16);
                                this.page.elements.push({
                                    kind: 'rect',
                                    x,
                                    y: this.y,
                                    width: cellWidth,
                                    height,
                                    fill: PAPER,
                                    radius: 4,
                                });
                                if (card.status)
                                    this.page.elements.push(...statusIcon(x + 16, this.y + 17, labelSize + 2, card.status));
                                let y = this.y + 16;
                                for (const line of card.labels) {
                                    this.line(line, x + 16 + (card.status ? labelSize + 9 : 0), y, labelSize, MUTED);
                                    y += labelSize * 1.4;
                                }
                                for (const line of card.values) {
                                    this.line(line, x + 16, y, valueSize, this.accent);
                                    y += valueSize * 1.3;
                                }
                                for (const line of card.details) {
                                    this.line(line, x + 16, y, labelSize, MUTED);
                                    y += labelSize * 1.4;
                                }
                            }
                            this.y += height + 16;
                        }
                        break;
                    }
                }
                for (const item of block.items) {
                    const status = statusAssessment(item.value) || statusAssessment(item.label);
                    const labelLines = wrapRuns(inlineRuns(item.label, 'bold'), this.contentWidth - 36 - (status ? this.bodySize + 9 : 0), this.bodySize);
                    const valueLines = wrapRuns(inlineRuns(item.value, 'bold'), this.contentWidth - 36, this.bodySize + 13);
                    const detailLines = item.detail
                        ? wrapRuns(inlineRuns(item.detail), this.contentWidth - 36, this.bodySize - 1)
                        : [];
                    const height = 30 +
                        labelLines.length * this.bodySize * 1.4 +
                        valueLines.length * (this.bodySize + 13) * 1.3 +
                        detailLines.length * this.bodySize * 1.4;
                    if (height > this.bottom - this.contentTop) {
                        this.heading(item.label);
                        this.paragraph(String(item.value ?? ''), { size: this.bodySize + 13, style: 'bold' });
                        if (item.detail)
                            this.paragraph(item.detail);
                        continue;
                    }
                    this.ensure(height + 14);
                    this.page.elements.push({
                        kind: 'rect',
                        x: this.margin,
                        y: this.y,
                        width: this.contentWidth,
                        height,
                        fill: PAPER,
                        radius: 4,
                    });
                    if (status)
                        this.page.elements.push(...statusIcon(this.margin + 18, this.y + 15, this.bodySize + 2, status));
                    let y = this.y + 14;
                    for (const line of labelLines) {
                        this.line(line, this.margin + 18 + (status ? this.bodySize + 9 : 0), y, this.bodySize, MUTED);
                        y += this.bodySize * 1.4;
                    }
                    for (const line of valueLines) {
                        this.line(line, this.margin + 18, y, this.bodySize + 13, this.accent);
                        y += (this.bodySize + 13) * 1.3;
                    }
                    for (const line of detailLines) {
                        this.line(line, this.margin + 18, y, this.bodySize - 1, MUTED);
                        y += this.bodySize * 1.4;
                    }
                    this.y += height + 14;
                }
                break;
            }
            case 'bars': {
                const minimum = Math.min(...block.items.map((item) => item.value ?? 0), 0);
                const percentageScale = block.items.some((item) => item.value !== null) &&
                    block.items.every((item) => item.value === null ||
                        (item.value >= 0 &&
                            item.value <= 100 &&
                            /^\d+(\.\d+)?%$/.test(item.display?.trim() || '') &&
                            Number(item.display.trim().slice(0, -1)) === item.value));
                const maximum = percentageScale
                    ? 100
                    : Math.max(...block.items.map((item) => item.value ?? 0), 1);
                const labelWidth = this.contentWidth * 0.3;
                const valueWidth = this.contentWidth * 0.25;
                const barWidth = this.contentWidth - labelWidth - valueWidth - 32;
                const size = this.view === 'slides' ? 18 : this.bodySize - 1;
                const padding = this.view === 'slides' ? 8 : 14;
                const items = block.items.map((item) => ({
                    item,
                    labels: wrapRuns(inlineRuns(item.label), labelWidth, size),
                    values: wrapRuns(inlineRuns(item.display || (item.value === null ? 'Not assessed' : String(item.value))), valueWidth, size),
                }));
                let index = 0;
                while (index < items.length) {
                    let rowHeight = Math.max(36, Math.max(items[index].labels.length, items[index].values.length) * size * 1.4 + padding);
                    if (rowHeight > this.bottom - this.contentTop) {
                        this.paragraph(items[index].item.label);
                        this.paragraph(items[index].item.display ||
                            (items[index].item.value === null
                                ? 'Not assessed'
                                : String(items[index].item.value)));
                        items[index].labels = [];
                        items[index].values = [];
                        rowHeight = 36;
                    }
                    this.ensure(rowHeight);
                    const group = [items[index++]];
                    while (index < items.length) {
                        const nextHeight = Math.max(rowHeight, Math.max(items[index].labels.length, items[index].values.length) * size * 1.4 +
                            padding);
                        if (this.y + (group.length + 1) * nextHeight > this.bottom)
                            break;
                        rowHeight = nextHeight;
                        group.push(items[index++]);
                    }
                    const chartId = `${block.id}-${this.page.id}-${index}`;
                    const barHeight = Math.max(12, rowHeight / 6);
                    const x = this.margin + labelWidth + 16;
                    const chartY = this.y;
                    for (let row = 0; row < group.length; row++) {
                        const current = group[row];
                        const y = chartY + row * rowHeight;
                        current.labels.forEach((line, lineIndex) => this.line(line, this.margin, y + lineIndex * size * 1.4 + 5, size));
                        current.values.forEach((line, lineIndex) => this.line(line, x + barWidth + 16, y + lineIndex * size * 1.4 + 5, size, MUTED));
                        const barY = y + (rowHeight - barHeight) / 2;
                        this.page.elements.push({
                            kind: 'rect',
                            x,
                            y: barY,
                            width: barWidth,
                            height: barHeight,
                            fill: LINE,
                        });
                        if (current.item.value !== null && current.item.value !== 0) {
                            const zero = x + (barWidth * -minimum) / (maximum - minimum);
                            const end = x + (barWidth * (current.item.value - minimum)) / (maximum - minimum);
                            this.page.elements.push({
                                kind: 'rect',
                                x: Math.min(zero, end),
                                y: barY,
                                width: Math.abs(end - zero),
                                height: barHeight,
                                fill: this.accent,
                                chartId,
                            });
                        }
                    }
                    if (group.some((item) => item.item.value !== null)) {
                        this.page.charts ||= [];
                        this.page.charts.push({
                            kind: 'bars',
                            id: chartId,
                            x,
                            y: chartY,
                            width: barWidth,
                            height: group.length * rowHeight,
                            minimum,
                            maximum,
                            rowHeight,
                            barHeight,
                            accent: this.accent,
                            items: group.map((item) => ({ label: item.item.label, value: item.item.value })),
                        });
                    }
                    this.y += group.length * rowHeight;
                    if (index < items.length)
                        this.nextPage();
                }
                this.gap(14);
                if (block.caption)
                    this.paragraph(block.caption, { size: this.bodySize - 2, color: MUTED });
                break;
            }
            case 'swot': {
                const cellWidth = (this.contentWidth - 18) / 2;
                const groupSize = this.view === 'report' ? 13 : 20;
                const cards = block.groups.map((group) => ({
                    group,
                    lines: group.items.flatMap((item) => wrapRuns(inlineRuns(`• ${item}`), cellWidth - 32, groupSize)),
                }));
                const pairHeights = Array.from({ length: Math.ceil(cards.length / 2) }, (_, index) => Math.max(...cards
                    .slice(index * 2, index * 2 + 2)
                    .map((card) => 66 + Math.max(card.lines.length, 1) * groupSize * 1.45)));
                if (this.layout === 'matrix' &&
                    pairHeights.every((height) => height <= this.bottom - this.contentTop)) {
                    for (let row = 0; row < pairHeights.length; row++) {
                        const height = pairHeights[row];
                        this.ensure(height + 18);
                        for (let col = 0; col < 2; col++) {
                            const card = cards[row * 2 + col];
                            if (!card)
                                continue;
                            const x = this.margin + col * (cellWidth + 18);
                            this.page.elements.push({
                                kind: 'rect',
                                x,
                                y: this.y,
                                width: cellWidth,
                                height,
                                fill: PAPER,
                                stroke: LINE,
                                radius: 4,
                            });
                            this.page.elements.push({
                                kind: 'rect',
                                x,
                                y: this.y,
                                width: cellWidth,
                                height: 4,
                                fill: this.accent,
                            });
                            this.page.elements.push(textElement(card.group.label, x + 16, this.y + 18, cellWidth - 32, groupSize + 3, 'bold', this.accent));
                            const lines = card.lines.length
                                ? card.lines
                                : [[{ text: 'No assessed items.', style: 'normal' }]];
                            lines.forEach((line, index) => this.line(line, x + 16, this.y + 53 + index * groupSize * 1.45, groupSize));
                        }
                        this.y += height + 18;
                    }
                    break;
                }
                for (const group of block.groups) {
                    this.heading(group.label);
                    if (!group.items.length)
                        this.paragraph('No assessed items.', { color: MUTED });
                    else
                        this.markdown(group.items.map((item) => `- ${item}`).join('\n'));
                }
                break;
            }
            case 'forces': {
                const gap = 18;
                const labelSize = this.view === 'slides' ? 22 : 16;
                const valueSize = this.view === 'slides' ? 25 : 18;
                const labelHeight = labelSize * 1.4;
                const valueHeight = valueSize * 1.45;
                const cardWidth = (this.contentWidth - gap) / 2;
                const cards = block.items.map((item) => {
                    const labels = wrapRuns(inlineRuns(item.label, 'bold'), cardWidth - 36, labelSize);
                    const values = wrapRuns(inlineRuns(item.value), cardWidth - 36, valueSize);
                    return {
                        labels,
                        values,
                        height: 36 + labels.length * labelHeight + 8 + values.length * valueHeight,
                    };
                });
                const drawCard = (x, width, labels, values, height) => {
                    this.page.elements.push({
                        kind: 'rect',
                        x,
                        y: this.y,
                        width,
                        height,
                        fill: PAPER,
                        stroke: LINE,
                        radius: 4,
                    });
                    this.page.elements.push({
                        kind: 'rect',
                        x,
                        y: this.y,
                        width: 3,
                        height,
                        fill: this.accent,
                    });
                    let y = this.y + 18;
                    for (const line of labels) {
                        this.line(line, x + 18, y, labelSize, this.accent);
                        y += labelHeight;
                    }
                    y += 8;
                    for (const line of values) {
                        this.line(line, x + 18, y, valueSize);
                        y += valueHeight;
                    }
                };
                for (let index = 0; index < cards.length;) {
                    const card = cards[index];
                    if (card.height > this.bottom - this.contentTop) {
                        // Full-width continuation cards keep a long assessment readable and complete.
                        const item = block.items[index++];
                        let labels = wrapRuns(inlineRuns(item.label, 'bold'), this.contentWidth - 36, labelSize);
                        if (labels.length * labelHeight > (this.bottom - this.contentTop) / 3) {
                            this.paragraph(item.label, { size: labelSize, style: 'bold', color: this.accent });
                            labels = [];
                        }
                        const lines = wrapRuns(inlineRuns(item.value), this.contentWidth - 36, valueSize);
                        let offset = 0;
                        while (offset < lines.length) {
                            const overhead = 44 + labels.length * labelHeight;
                            this.ensure(overhead + valueHeight + gap);
                            const count = Math.max(1, Math.min(lines.length - offset, Math.floor((this.bottom - this.y - overhead - gap) / valueHeight)));
                            const height = overhead + count * valueHeight;
                            drawCard(this.margin, this.contentWidth, labels, lines.slice(offset, offset + count), height);
                            this.y += height + gap;
                            offset += count;
                            if (offset < lines.length)
                                this.nextPage();
                        }
                        continue;
                    }
                    const next = cards[index + 1];
                    const pair = next && next.height <= this.bottom - this.contentTop ? [card, next] : [card];
                    const height = Math.max(...pair.map((item) => item.height));
                    this.ensure(height + gap);
                    pair.forEach((item, column) => drawCard(this.margin + column * (cardWidth + gap), cardWidth, item.labels, item.values, height));
                    this.y += height + gap;
                    index += pair.length;
                }
                if (block.caption)
                    this.paragraph(block.caption, { size: this.bodySize - 2, color: MUTED });
                break;
            }
        }
        this.gap(14);
        this.pages.slice(firstPage).forEach((page, index) => {
            page.elements.slice(index === 0 ? firstElement : 0).forEach((element) => {
                element.blockId = block.id;
            });
        });
    }
    sectionContent(render) {
        const initialPage = this.page;
        const initialCount = this.page.elements.length;
        render();
        if (this.page === initialPage && this.page.elements.length === initialCount) {
            const message = this.sectionId === 'decisions'
                ? 'No decisions or next actions have been recorded.'
                : this.view === 'report'
                    ? 'No content has been recorded for this section.'
                    : 'No content has been selected or written for this slide.';
            this.paragraph(message, { color: MUTED });
        }
    }
}
function addCover(composer, coverSlide) {
    const { doc, width, height, margin, accent } = composer;
    const title = coverSlide?.title || doc.metadata.title;
    composer.start('cover', title, coverSlide?.id, coverSlide?.notes);
    composer.page.elements = [
        { kind: 'rect', x: 0, y: 0, width, height, fill: '#ffffff' },
        { kind: 'rect', x: margin, y: 72, width: 56, height: 6, fill: accent },
    ];
    const company = doc.sourceSnapshot.company;
    const companyWidth = width - 2 * margin - (company.logo ? 160 : 0);
    const companySize = Math.min(17, companyWidth / Math.max(measureReportText(company.name, 1, 'bold'), 1));
    composer.page.elements.push(textElement(company.name, margin, 105, companyWidth, companySize, 'bold', MUTED));
    if (company.logo)
        composer.page.elements.push({
            kind: 'image',
            x: width - margin - 130,
            y: 65,
            width: 130,
            height: 65,
            src: company.logo,
            alt: `${company.name} logo`,
        });
    composer.y = composer.view === 'report' ? 360 : 230;
    composer.paragraph(title, { size: composer.view === 'report' ? 48 : 58, style: 'bold', gap: 24 });
    if (doc.metadata.subtitle)
        composer.paragraph(doc.metadata.subtitle, {
            size: composer.view === 'report' ? 22 : 28,
            color: MUTED,
        });
    if (doc.metadata.author)
        composer.paragraph(`Prepared by ${doc.metadata.author}`, { size: 14, color: MUTED });
    if (doc.metadata.date)
        composer.paragraph(doc.metadata.date, { size: 14, color: MUTED });
    if (coverSlide?.takeaways.trim())
        composer.markdown(coverSlide.takeaways);
}
function decoratePages(doc, pages) {
    pages.forEach((page, index) => {
        page.pageNumber = index + 1;
        const margin = page.width === 794 ? 56 : 72;
        if (doc.settings.includeFooter) {
            page.elements.push({
                kind: 'line',
                x: margin,
                y: page.height - 48,
                x2: page.width - margin,
                y2: page.height - 48,
                color: LINE,
                width: 1,
            });
            const label = doc.settings.confidential
                ? 'CONFIDENTIAL · Prepared with StratPlan'
                : 'Prepared with StratPlan';
            page.elements.push(textElement(label, margin, page.height - 34, page.width - 2 * margin - 70, 10, 'normal', MUTED));
        }
        if (doc.settings.includePageNumbers)
            page.elements.push(textElement(`${index + 1} / ${pages.length}`, page.width - margin - 70, page.height - 34, 70, 10, 'normal', MUTED, 'right'));
    });
}
export function compileReportLayout(doc, view) {
    const composer = new Composer(doc, view);
    if (view === 'report' && doc.settings.includeCover)
        addCover(composer);
    if (view === 'slides') {
        for (const slide of doc.slides.filter((item) => item.enabled)) {
            if (slide.sectionId === 'cover') {
                if (doc.settings.includeCover)
                    addCover(composer, slide);
                continue;
            }
            const section = doc.sections.find((item) => item.id === slide.sectionId);
            if (!section)
                continue;
            composer.start(section.id, slide.title || section.title, slide.id, slide.notes, slide.layout);
            composer.sectionContent(() => {
                if (slide.takeaways.trim())
                    composer.takeaways(slide.takeaways);
                for (const blockId of slide.blockIds) {
                    const block = section.blocks.find((item) => item.id === blockId);
                    if (block)
                        composer.block(block);
                }
            });
        }
    }
    else {
        for (const section of doc.sections.filter((item) => item.enabled)) {
            composer.start(section.id, section.title, undefined, undefined, section.layout);
            composer.sectionContent(() => {
                for (const block of section.blocks)
                    if (block.enabled !== false)
                        composer.block(block);
            });
        }
        if (doc.settings.includeContents && doc.sections.some((section) => section.enabled)) {
            const entries = doc.sections
                .filter((section) => section.enabled)
                .map((section) => ({
                section,
                first: composer.pages.findIndex((page) => page.sectionId === section.id),
            }));
            // Contents count influences every following page number. Iterate until the
            // count is stable, then insert the final fully laid-out contents pages.
            let count = 1;
            let contentsPages = [];
            for (let attempt = 0; attempt < 8; attempt++) {
                const candidate = new Composer(doc, view);
                candidate.start('contents', 'Contents');
                for (const { section, first } of entries) {
                    const size = 17;
                    const lines = wrapRuns(inlineRuns(section.title), candidate.contentWidth - 75, size);
                    const height = lines.length * size * 1.45 + 28;
                    if (height > candidate.bottom - candidate.contentTop) {
                        candidate.paragraph(section.title);
                        candidate.paragraph(`Page ${first + 1 + count}`);
                        continue;
                    }
                    candidate.ensure(height);
                    lines.forEach((line, index) => candidate.line(line, candidate.margin + 14, candidate.y + index * size * 1.45 + 10, size));
                    const sectionStyle = reportSectionStyle(section.id);
                    candidate.page.elements.push({
                        kind: 'rect',
                        x: candidate.margin,
                        y: candidate.y + 10,
                        width: 4,
                        height: size * 1.3,
                        fill: sectionStyle.color,
                    });
                    candidate.page.elements.push(textElement(String(first + 1 + count), candidate.width - candidate.margin - 60, candidate.y + 10, 60, size, 'bold', sectionStyle.color, 'right'));
                    candidate.page.elements.push({
                        kind: 'line',
                        x: candidate.margin,
                        y: candidate.y + height - 1,
                        x2: candidate.width - candidate.margin,
                        y2: candidate.y + height - 1,
                        color: LINE,
                        width: 0.8,
                    });
                    candidate.y += height;
                }
                contentsPages = candidate.pages;
                if (candidate.pages.length === count)
                    break;
                count = candidate.pages.length;
            }
            composer.pages.splice(doc.settings.includeCover ? 1 : 0, 0, ...contentsPages);
        }
    }
    decoratePages(doc, composer.pages);
    return composer.pages;
}
